<script setup>
import { ref, onBeforeMount, onBeforeUnmount, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { useToast } from "vue-toast-notification";
import LoadingSpinner from "../components/LoadingSpinner.vue";

const store = useStore();
const toast = useToast();

const apiEndpoint = store.state.apiEndpoint;
const token = sessionStorage.getItem("token");
const headers = {
  headers: {
    Authorization: `Bearer ${token}`,
  },
};

const loading = ref(false);
const creating = ref(false);
const trades = ref([]);
const newTrade = ref({
  code: "",
  description: "",
});

const toEditableTrade = (trade) => ({
  ...trade,
  _dirty: false,
  _saving: false,
  _deleting: false,
});

const normalizeCode = (value) => (value || "").trim().toUpperCase();
const normalizeDescription = (value) => (value || "").trim();
const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.code) && data.code[0]) return data.code[0];
  if (Array.isArray(data.description) && data.description[0]) return data.description[0];
  return fallback;
};

const fetchTrades = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${apiEndpoint}/trades/`, headers);
    trades.value = (response.data || []).map(toEditableTrade);
  } catch (error) {
    toast.error("Failed to load trades");
  } finally {
    loading.value = false;
  }
};

const addTrade = async () => {
  const code = normalizeCode(newTrade.value.code);
  const description = normalizeDescription(newTrade.value.description);
  if (!code || !description) {
    toast.error("Code and description are required");
    return;
  }

  creating.value = true;
  try {
    const response = await axios.post(
      `${apiEndpoint}/trades/`,
      { code, description },
      headers
    );
    trades.value.push(toEditableTrade(response.data));
    trades.value.sort((a, b) => a.code.localeCompare(b.code));
    newTrade.value = { code: "", description: "" };
    toast.success("Trade created");
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to create trade"));
  } finally {
    creating.value = false;
  }
};

const saveTrade = async (trade) => {
  const code = normalizeCode(trade.code);
  const description = normalizeDescription(trade.description);
  if (!code || !description) {
    toast.error("Code and description are required");
    return;
  }

  trade._saving = true;
  try {
    const response = await axios.put(
      `${apiEndpoint}/trades/${trade.id}/`,
      { code, description },
      headers
    );
    Object.assign(trade, response.data, { _dirty: false });
    trades.value.sort((a, b) => a.code.localeCompare(b.code));
    toast.success("Trade updated");
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to update trade"));
  } finally {
    trade._saving = false;
  }
};

const deleteTrade = async (trade) => {
  if (!window.confirm(`Delete trade ${trade.code}?`)) {
    return;
  }

  trade._deleting = true;
  try {
    await axios.delete(`${apiEndpoint}/trades/${trade.id}/`, headers);
    trades.value = trades.value.filter((row) => row.id !== trade.id);
    toast.success("Trade deleted");
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to delete trade"));
  } finally {
    trade._deleting = false;
  }
};

onBeforeMount(() => {
  store.state.imageLayout = "profile-overview";
  store.state.showNavbar = true;
  store.state.showFooter = false;
  store.state.isAbsolute = true;
  store.state.hideConfigButton = true;
  store.state.currentRoute = {
    title: "Trades",
    icon: "fa-tags",
    color: "warning",
  };
});

onBeforeUnmount(() => {
  store.state.isAbsolute = false;
  store.state.imageLayout = "default";
  store.state.showNavbar = true;
  store.state.showFooter = false;
  store.state.hideConfigButton = false;
});

onMounted(async () => {
  await fetchTrades();
});
</script>

<template>
  <LoadingSpinner v-if="loading" message="Loading trades..." :fullScreen="true" />
  <div class="container-fluid">
    <div
      class="page-header min-height-300"
      style="margin-right: -24px; margin-left: -34%"
      :style="{ backgroundImage: 'url(' + require('@/assets/img/houses2.jpg') + ')' }"
    >
      <span class="mask bg-gradient-warning opacity-6"></span>
    </div>
  </div>
  <div class="py-4 container-fluid mt-n6">
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header p-3 pb-0">
            <h5 class="mb-0">Trade Management</h5>
            <p class="text-sm text-muted mb-0">Manage code/description pairs used in scope trade tags.</p>
          </div>
          <div class="card-body">
            <div class="row g-2 align-items-end mb-4">
              <div class="col-md-3">
                <label class="form-label">Code</label>
                <input
                  v-model="newTrade.code"
                  type="text"
                  class="form-control"
                  placeholder="ELEC"
                />
              </div>
              <div class="col-md-7">
                <label class="form-label">Description</label>
                <input
                  v-model="newTrade.description"
                  type="text"
                  class="form-control"
                  placeholder="Electrical"
                />
              </div>
              <div class="col-md-2">
                <button class="btn btn-success w-100 mb-0" :disabled="creating" @click="addTrade">
                  Add Trade
                </button>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Code</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Description</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="trade in trades" :key="trade.id">
                    <td>
                      <input
                        v-model="trade.code"
                        type="text"
                        class="form-control"
                        @input="trade._dirty = true"
                      />
                    </td>
                    <td>
                      <input
                        v-model="trade.description"
                        type="text"
                        class="form-control"
                        @input="trade._dirty = true"
                      />
                    </td>
                    <td class="text-end">
                      <button
                        class="btn btn-primary btn-sm mb-0 me-2"
                        :disabled="trade._saving || trade._deleting || !trade._dirty"
                        @click="saveTrade(trade)"
                      >
                        Save
                      </button>
                      <button
                        class="btn btn-outline-danger btn-sm mb-0"
                        :disabled="trade._saving || trade._deleting"
                        @click="deleteTrade(trade)"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="!loading && trades.length === 0" class="text-muted text-sm mt-3">
              No trades found.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
